import type React from 'react'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { trackEvent } from '../../utils/events/trackEvent'
import '../../utils/privacy-compliance/oneTrustTestHelpers.utils' // Expose test helpers to window
import { OneTrustCategory } from '../../utils/privacy-compliance/oneTrustConsent.utils'

interface OneTrustConfig {
  oneTrust?: {
    allowCookieOptOut: boolean
    includeAutoBlockScript: boolean
    domainUuid: string
    reverse_banner_button_position?: string
    assetDomain?: string
  }
}

interface OneTrustCookieBannerProps {
  /** OneTrust configuration from store settings */
  config: OneTrustConfig | null
  /** Whether the banner should be enabled */
  enabled?: boolean
}

const validateAssetDomain = (assetDomain: string) =>
  /^[a-zA-Z0-9.-]+\.(cookielaw\.org|onetrust\.com)$/.test(assetDomain)

/**
 * OneTrust Cookie Banner Component
 *
 * Loads and initializes OneTrust cookie consent management based on configuration.
 * This component handles:
 * - Loading OneTrust scripts dynamically
 * - Initializing OneTrust with provided configuration
 * - Tracking banner interactions for analytics
 */
export const OneTrustCookieBanner: React.FunctionComponent<OneTrustCookieBannerProps> = ({
  config,
  enabled = true,
}) => {
  const intl = useIntl()
  const [isInitialized, setIsInitialized] = useState(false)
  const oneTrustConfig = config?.oneTrust
  const domainUuid = oneTrustConfig?.domainUuid
  const includeAutoBlockScript = oneTrustConfig?.includeAutoBlockScript || false

  let assetDomain = 'cdn.cookielaw.org'
  if (validateAssetDomain(oneTrustConfig?.assetDomain)) {
    assetDomain = oneTrustConfig?.assetDomain
  }

  useEffect(() => {
    console.log('domainUuid', domainUuid)
    if (!domainUuid || !enabled || isInitialized) {
      return
    }

    // Get app's locale from React Intl (synced with localStorage)
    const appLocale = intl.locale
    // Convert locale format: en-US → en, es-ES → es, fr-FR → fr
    const oneTrustLanguage = appLocale.split('-')[0]

    console.log(
      '[OneTrust Banner] Initializing with language:',
      oneTrustLanguage,
      'from app locale:',
      appLocale
    )

    try {
      // Add OneTrust auto-block script if enabled
      if (includeAutoBlockScript) {
        const existingAutoBlockScript = document.querySelector(
          `script[src*="/consent/${domainUuid}/OtAutoBlock.js"]`
        )

        if (!existingAutoBlockScript) {
          const autoBlockScript = document.createElement('script')
          autoBlockScript.src = `https://${assetDomain}/consent/${domainUuid}/OtAutoBlock.js`
          autoBlockScript.type = 'text/javascript'
          autoBlockScript.charset = 'UTF-8'
          document.head.appendChild(autoBlockScript)
        }
      }

      // Add OneTrust main script
      const existingMainScript = document.querySelector(
        `script[data-domain-script="${domainUuid}"]`
      )

      if (!existingMainScript) {
        const mainScript = document.createElement('script')
        mainScript.src = `https://${assetDomain}/scripttemplates/otSDKStub.js`
        mainScript.type = 'text/javascript'
        mainScript.charset = 'UTF-8'
        mainScript.setAttribute('data-domain-script', domainUuid)
        mainScript.setAttribute('data-language', oneTrustLanguage)

        mainScript.onload = () => {
          const existingWrapper = window.OptanonWrapper

          window.OptanonWrapper = function () {
            if (existingWrapper && typeof existingWrapper === 'function') {
              existingWrapper()
            }

            const activeGroups = window.OnetrustActiveGroups || ''
            console.log('[OneTrust Banner] OptanonWrapper called:', {
              activeGroups,
              timestamp: new Date().toISOString(),
            })

            // Dispatch custom event to notify listeners of consent change
            window.dispatchEvent(new CustomEvent('consent.onetrust'))

            trackEvent({
              id: 'privacy.onetrust.consent.changed',
              description: 'OneTrust consent preferences changed',
              data: {
                activeGroups: activeGroups,
                hasAnalyticsConsent: activeGroups.includes(`,${OneTrustCategory.PERFORMANCE},`),
              },
            })
          }

          trackEvent({
            id: 'privacy.onetrust.banner.initialized',
            description: 'OneTrust cookie banner initialized',
            data: {
              domainUuid: domainUuid,
              allowCookieOptOut: oneTrustConfig?.allowCookieOptOut,
              includeAutoBlockScript: includeAutoBlockScript,
            },
          })

          setIsInitialized(true)
        }

        mainScript.onerror = error => {
          console.error('Failed to load OneTrust script:', error)

          trackEvent({
            id: 'privacy.onetrust.banner.error',
            description: 'OneTrust banner script failed to load',
            data: {
              domainUuid: domainUuid,
              error: 'Script load failed',
            },
          })
        }

        document.head.appendChild(mainScript)
      }
    } catch (error) {
      console.error('Failed to initialize OneTrust banner:', error)

      trackEvent({
        id: 'privacy.onetrust.banner.initialization_error',
        description: 'OneTrust banner initialization failed',
        data: {
          error: error instanceof Error ? error.message : 'Unknown error',
          domainUuid: domainUuid,
        },
      })
    }
  }, [
    domainUuid,
    assetDomain,
    includeAutoBlockScript,
    oneTrustConfig,
    enabled,
    isInitialized,
    intl.locale,
  ])

  // Dynamically change OneTrust language when app locale changes
  useEffect(() => {
    if (!isInitialized || !window.OneTrust?.changeLanguage) {
      return
    }

    const appLocale = intl.locale
    const oneTrustLanguage = appLocale.split('-')[0]

    // Only change language if not English (avoid unnecessary API calls)
    if (oneTrustLanguage === 'en') {
      return
    }

    console.log('[OneTrust Banner] Attempting to change language to:', oneTrustLanguage)

    try {
      window.OneTrust.changeLanguage(oneTrustLanguage)

      // Wait a bit to check if the language change was successful
      setTimeout(() => {
        try {
          const domainData = window.OneTrust?.GetDomainData()
          const currentLanguage = domainData?.Language?.Culture || 'unknown'

          if (currentLanguage.startsWith(oneTrustLanguage)) {
            console.log('[OneTrust Banner] ✅ Language changed successfully to:', oneTrustLanguage)
            trackEvent({
              id: 'privacy.onetrust.language.changed',
              description: 'OneTrust banner language changed',
              data: {
                locale: appLocale,
                language: oneTrustLanguage,
                success: true,
              },
            })
          } else {
            console.warn(
              `[OneTrust Banner] ⚠️ Language '${oneTrustLanguage}' not available. ` +
                `Translations may not be published in OneTrust portal. ` +
                `Banner will display in English. Please publish translations at: https://app.onetrust.com/`
            )
            trackEvent({
              id: 'privacy.onetrust.language.fallback',
              description: 'OneTrust banner language fallback to English',
              data: {
                requestedLocale: appLocale,
                requestedLanguage: oneTrustLanguage,
                actualLanguage: currentLanguage,
              },
            })
          }
        } catch (checkError) {
          console.warn('[OneTrust Banner] Could not verify language change:', checkError)
        }
      }, 500)
    } catch (error) {
      console.error('[OneTrust Banner] Failed to change language:', error)
      trackEvent({
        id: 'privacy.onetrust.language.error',
        description: 'OneTrust banner language change failed',
        data: {
          locale: appLocale,
          language: oneTrustLanguage,
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      })
    }
  }, [intl.locale, isInitialized])

  // This component doesn't render any visible UI - OneTrust handles the banner display
  return null
}

export default OneTrustCookieBanner
